import { createTimeInstance } from '@utils/time';
import { LOADED_WINDOW_PRESETS } from '@features/scoreBackfill/windowPresets';
import { formatUtcDate, knowableRange } from './format';

// The date arithmetic behind the table's knowable-at filter. It lives apart from rowFilters.js so
// that module can stay a list of predicates, and apart from the component so the awkward part —
// which zone a "day" is measured in — is testable without a DOM.
//
// THE ONE CONTRACT, because two zones meet here and getting it wrong is invisible until a boundary
// row goes missing:
//
//   A window bound is a CALENDAR DATE, carried as a Date at LOCAL midnight.
//
// That is what react-date-picker hands back and what DateWindow's presets compute in, so the whole
// picking side of this feature already speaks it. But the column being filtered is `Knowable at
// (UTC)`, so the calendar date "Oct 31" has to mean 2025-10-31T00:00:00Z through
// 2025-10-31T23:59:59.999Z — never local midnight, which in a negative-offset zone would drop a row
// stamped 02:00Z and pick up one stamped the next day. So every bound crosses into UTC through
// utcDayStart/utcDayEnd below: read the LOCAL year-month-day (that is the date the calendar showed)
// and rebuild it with Date.UTC.
//
// Deliberately not createTimeInstance(d).startOf('day') — the trap `format.js` documents twice: this
// project's dayjs has no utc plugin, so that yields a local midnight. `toIsoStart`/`toIsoEnd` in
// services/feedView.js do exactly that, which is tolerable for a request the server re-filters and
// is not tolerable here, where this predicate is the only thing deciding what is on screen.

const DAY_MS = 24 * 60 * 60 * 1000;

// A picked calendar date -> the first instant of the UTC day it names, in ms.
export const utcDayStart = (date) => {
  if (!date) return null;
  const local = date instanceof Date ? date : new Date(date);
  if (isNaN(local.getTime())) return null;
  return Date.UTC(local.getFullYear(), local.getMonth(), local.getDate());
};

// ...and the last, inclusive. Inclusive rather than exclusive-next-midnight because both bounds come
// off the same calendar and an analyst who picks Oct 31 on both sides means that whole day.
export const utcDayEnd = (date) => {
  const start = utcDayStart(date);
  return start === null ? null : start + DAY_MS - 1;
};

// An ISO instant from a row -> the calendar date it falls on IN UTC, carried per the contract above.
// This is the conversion that lets the pickers' bounds agree with the column: a row stamped
// 2026-01-30T07:01Z names the calendar date Jan 30 wherever the viewer is sitting.
export const calendarDateOfStamp = (value) => {
  if (!value) return null;
  const instant = new Date(value);
  if (isNaN(instant.getTime())) return null;
  return new Date(instant.getUTCFullYear(), instant.getUTCMonth(), instant.getUTCDate());
};

// The calendar dates the loaded rows actually span. Built on knowableRange rather than re-scanning:
// that function already answers "which knowable-at span do these rows cover", and the table's
// summary memo already calls it for the `covering X → Y` line.
//
// This is both the pickers' min/max and the preset anchor, which is the point — a filter over loaded
// rows must not be able to select a day the rows cannot contain.
export const loadedSpan = (rows) => {
  const range = knowableRange(rows);
  if (!range) return null;
  const from = calendarDateOfStamp(range.from);
  const to = calendarDateOfStamp(range.to);
  if (!from || !to) return null;
  return { from, to };
};

// How many calendar days the span covers, counting both ends. One means every loaded row shares a
// single knowable-at day, and there is nothing for a date filter to narrow.
export const spanDays = (span) => {
  if (!span) return 0;
  const from = utcDayStart(span.from);
  const to = utcDayStart(span.to);
  if (from === null || to === null) return 0;
  return Math.round((to - from) / DAY_MS) + 1;
};

export const isNarrowable = (span) => spanDays(span) > 1;

// Whether a preset would actually narrow these rows. A "90d" over sixty days of rows reproduces the
// unfiltered table, and a chip that cannot change what is on screen is worse than no chip — the same
// rule TableControls already applies to the sector and index selects.
//
// Measured with dayjs against the span's own upper bound, in the same local-calendar terms
// DateWindow resolves the preset in, so what is offered and what it computes cannot disagree.
const narrowsSpan = (preset, span) => {
  const upper = createTimeInstance(span.to);
  const lower = createTimeInstance(span.from);
  if (preset.amount) return upper.subtract(preset.amount, preset.unit).isAfter(lower);
  // YTD only means something if the span reaches back past January 1st of the newest row's year.
  if (preset.key === 'ytd') return upper.startOf('year').isAfter(lower);
  // Latest, All and Custom always earn their place: on a narrowable span, one day is a narrowing,
  // clearing is the way back, and Custom is the only route to a window no preset spells.
  return true;
};

// The chips to offer for these rows. On a span of one day (or none) only All and Custom survive —
// All so an existing window can be cleared, Custom so it can still be seen.
export const windowPresetsFor = (span) => {
  if (!isNarrowable(span)) return LOADED_WINDOW_PRESETS.filter((preset) => preset.clears || preset.key === 'custom');
  return LOADED_WINDOW_PRESETS.filter((preset) => narrowsSpan(preset, span));
};

// The collapsed chip's label. Formatted from the DERIVED UTC bounds rather than from the picked
// Dates, so it names the same day the column does: formatUtcDate renders in UTC, and a local
// midnight handed to it reads as the previous day west of Greenwich.
//
// A one-day window prints its date rather than the word "Latest". The pressed chip in the panel
// already says which preset produced it; the collapsed label's job is to say WHAT is selected, and a
// date survives the panel being closed and the rows being re-read.
export const describeWindow = ({ knowableFrom = null, knowableTo = null } = {}) => {
  const from = utcDayStart(knowableFrom);
  const to = utcDayStart(knowableTo);
  if (from === null && to === null) return 'All dates';
  if (from !== null && to !== null) {
    if (from === to) return formatUtcDate(from);
    return `${formatUtcDate(from)} → ${formatUtcDate(to)}`;
  }
  return from !== null ? `from ${formatUtcDate(from)}` : `to ${formatUtcDate(to)}`;
};
